import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Avatar,
    Chip,
    IconButton,
    CircularProgress,
    Divider,
    Alert,
    LinearProgress,
    Fade,
    AppBar,
    Toolbar
} from "@mui/material";
import {
    Send as SendIcon,
    Refresh as RefreshIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon,
    Person as PersonIcon,
    AttachFile as AttachFileIcon
} from "@mui/icons-material";

import { ChatbotScriptEngine } from "../utils/chatbotScriptEngine";
import { ProjectSetupData } from "./AIProjectSetupModal";
import Component250418IntIcon from "../imports/250418IntIcon-13029-85";
import { CompactFileUpload } from "./common/CompactFileUpload";

interface ProjectSetupChatbotProps {
    onProjectSetup?: (data: ProjectSetupData) => void;
    onClose?: () => void;
}

interface ChatMessage {
    id: string;
    role: "user" | "assistant";
    content: string;
    timestamp: Date;
    stepId?: string;
}

interface UploadedFile {
    id: string;
    name: string;
    size: number;
    type: string;
}

export const ProjectSetupChatbot: React.FC<ProjectSetupChatbotProps> = ({ onProjectSetup, onClose }) => {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [currentInput, setCurrentInput] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [chatEngine] = useState(() => new ChatbotScriptEngine());
    const [showProjectSummary, setShowProjectSummary] = useState(false);
    const [showProjectPreview, setShowProjectPreview] = useState(false);
    const [showFileUpload, setShowFileUpload] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Initialize conversation
    useEffect(() => {
        const initialMessage = chatEngine.startConversation();
        setMessages([
            {
                id: "1",
                role: "assistant",
                content: initialMessage,
                timestamp: new Date(),
                stepId: "TYPE"
            }
        ]);
    }, [chatEngine]);

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSendMessage = async () => {
        if (!currentInput.trim() || isProcessing) return;

        const userMessage: ChatMessage = {
            id: Date.now().toString(),
            role: "user",
            content: currentInput.trim(),
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setCurrentInput("");
        setIsProcessing(true);

        // Simulate typing delay
        await new Promise(resolve => setTimeout(resolve, 800));

        try {
            const response = chatEngine.processUserInput(userMessage.content);

            const assistantMessage: ChatMessage = {
                id: (Date.now() + 1).toString(),
                role: "assistant",
                content: response,
                timestamp: new Date(),
                stepId: chatEngine.getCurrentStep() // This is the NEW step after processing
            };

            setMessages(prev => [...prev, assistantMessage]);

            // Update UI state based on conversation progress
            updateUIStateForCurrentStep();
        } catch (error) {
            const errorMessage: ChatMessage = {
                id: (Date.now() + 1).toString(),
                role: "assistant",
                content: "Sorry, something went wrong. Let me restart our conversation.",
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
            chatEngine.reset();
        }

        setIsProcessing(false);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleConfirmProject = async () => {
        const projectData = chatEngine.getProjectData();

        if (onProjectSetup && projectData) {
            // Show loading state
            setIsProcessing(true);
            setShowProjectSummary(false); // Hide summary during processing

            // Add a "Creating project..." message
            const creationMessage: ChatMessage = {
                id: Date.now().toString(),
                role: "assistant",
                content: "🚀 Creating your product...\n\nSetting up product structure and initial features. This will take a moment.",
                timestamp: new Date(),
                stepId: "CREATING"
            };
            setMessages(prev => [...prev, creationMessage]);

            // Simulate project creation with 3 second delay
            await new Promise(resolve => setTimeout(resolve, 3000));

            // Convert chatbot data to modal format
            const modalData: ProjectSetupData = {
                projectType: projectData.projectName || projectData.projectType || "New Project",
                uploadedFiles: [], // Files would be handled differently in real implementation
                targetUser: projectData.targetUser || "",
                painPoints: projectData.painPoints || "",
                softwareSolution: projectData.solution || "",
                teamComposition: projectData.team?.join(", ") || ""
            };

            onProjectSetup(modalData);

            // Close the modal after successful creation
            if (onClose) {
                onClose();
            }

            setIsProcessing(false);
        }
    };

    const handleRestart = () => {
        chatEngine.reset();
        const initialMessage = chatEngine.startConversation();
        setMessages([
            {
                id: Date.now().toString(),
                role: "assistant",
                content: initialMessage,
                timestamp: new Date(),
                stepId: "TYPE"
            }
        ]);
        setShowProjectSummary(false);
        setShowFileUpload(false);
    };

    const getStepIndicator = () => {
        const steps = [
            { id: "TYPE", label: "Type" },
            { id: "NAME", label: "Name" },
            { id: "FILES", label: "Files" },
            { id: "USER", label: "Users" },
            { id: "PROBLEMS", label: "Problems" },
            { id: "SOLUTION", label: "Solution" },
            { id: "REVIEW", label: "Review" }
        ];

        const currentStepIndex = steps.findIndex(step => step.id === chatEngine.getCurrentStep());

        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.5, // Decreased spacing between steps
                    mb: 2,
                    overflowX: "auto",
                    pb: 1,
                    justifyContent: "center"
                }}
            >
                {steps.map((step, index) => (
                    <React.Fragment key={step.id}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 0.5,
                                cursor: "pointer",
                                "&:hover": {
                                    opacity: 0.8
                                }
                            }}
                            onClick={() => {
                                // Make step clickable to resurface that step's prompt
                                const stepResponse = chatEngine.navigateToStep(step.id);
                                const assistantMessage: ChatMessage = {
                                    id: Date.now().toString(),
                                    role: "assistant",
                                    content: stepResponse,
                                    timestamp: new Date(),
                                    stepId: step.id
                                };
                                setMessages(prev => [...prev, assistantMessage]);
                            }}
                        >
                            {/* Icon positioned above text */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 28, // Slightly smaller for better proportions
                                    height: 28,
                                    borderRadius: "50%",
                                    bgcolor: index <= currentStepIndex ? "primary.main" : "action.disabled",
                                    color: index <= currentStepIndex ? "primary.contrastText" : "text.disabled",
                                    fontSize: "0.7rem",
                                    fontWeight: 600,
                                    flexShrink: 0,
                                    transition: "all 0.2s ease"
                                }}
                            >
                                {index < currentStepIndex ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : index + 1}
                            </Box>

                            {/* Text positioned beneath icon */}
                            <Typography
                                variant="caption"
                                sx={{
                                    color: index <= currentStepIndex ? "text.primary" : "text.disabled",
                                    fontWeight: index === currentStepIndex ? 600 : 400,
                                    fontSize: "0.7rem",
                                    textAlign: "center",
                                    lineHeight: 1.2,
                                    minWidth: "fit-content"
                                }}
                            >
                                {step.label}
                            </Typography>
                        </Box>

                        {index < steps.length - 1 && (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    height: 28, // Match icon height for alignment
                                    mt: 0 // Align with icons
                                }}
                            >
                                <Divider
                                    sx={{
                                        width: 16, // Shorter lines for decreased spacing
                                        height: 2,
                                        bgcolor: index < currentStepIndex ? "primary.main" : "action.disabled",
                                        flexShrink: 0
                                    }}
                                />
                            </Box>
                        )}
                    </React.Fragment>
                ))}
            </Box>
        );
    };

    const formatMessageContent = (content: string) => {
        // Enhanced formatting with better line break handling and no ** characters
        const cleanContent = content.replace(/\*\*/g, ""); // Remove ** characters
        const lines = cleanContent.split("\n");

        return lines
            .map((line, index) => {
                if (line.trim() === "") {
                    // Empty line - create spacing
                    return <Box key={index} sx={{ height: 8 }} />;
                } else if (line.startsWith("🏷️") || line.startsWith("👥") || line.startsWith("💡") || line.startsWith("⚠️") || line.startsWith("📁")) {
                    // Emoji-prefixed lines (summary items)
                    return (
                        <Typography key={index} variant="body2" sx={{ fontWeight: 500, mb: 1, lineHeight: 1.5 }}>
                            {line}
                        </Typography>
                    );
                } else if (line.startsWith("• ")) {
                    // Bullet points
                    return (
                        <Typography key={index} variant="body2" sx={{ ml: 2, mb: 0.5, lineHeight: 1.5 }}>
                            {line}
                        </Typography>
                    );
                } else if (line.includes("?") && line.length > 50) {
                    // Questions - make them stand out
                    return (
                        <Typography key={index} variant="body2" sx={{ fontWeight: 500, mb: 1, lineHeight: 1.5 }}>
                            {line}
                        </Typography>
                    );
                } else if (line.trim()) {
                    // Regular text with proper line height
                    return (
                        <Typography key={index} variant="body2" sx={{ mb: 0.5, lineHeight: 1.5 }}>
                            {line}
                        </Typography>
                    );
                }
                return null;
            })
            .filter(Boolean); // Remove null entries
    };

    // Dynamic chip rendering based on the last assistant message step
    const renderCurrentStepChips = () => {
        // Get the last assistant message to determine which step's chips to show
        const lastAssistantMessage = [...messages].reverse().find(msg => msg.role === "assistant");

        // Use the stepId from the last assistant message
        // This represents the step the assistant just presented to the user
        let stepToUse = lastAssistantMessage?.stepId;

        // Fallback: If no stepId on last message, use current engine step
        if (!stepToUse) {
            stepToUse = chatEngine.getCurrentStep();
        }

        if (!stepToUse) {
            return null;
        }

        // Get chips for the step from the last assistant message - EXACT text match with script
        const CHATBOT_STEPS = {
            TYPE: ["Existing Product", "New Product"],
            NAME: ["Sales Enablement Platform", "Q4 Analytics Dashboard", "Personalized Wellness App", "Equipment Maintenance Tracker"],
            FILES: [],
            FILE_ANALYSIS: ["Yes, proceed", "No, ignore analysis"],
            USER: [
                "Jordan, Sales Manager at a mid-sized SaaS company",
                "Maya, a 28-year-old fitness enthusiast and busy professional",
                "Sgt. Ramirez, Logistics Officer in the Army"
            ],
            PROBLEMS: [
                "Struggles to access up-to-date sales collateral",
                "Finds it hard to stay motivated without personalized guidance",
                "Manual tracking of maintenance schedules leads to errors"
            ],
            SOLUTION: [
                "A centralized sales enablement platform that helps B2B sales teams access content, track performance, and close deals faster",
                "A personalized wellness app that helps users track fitness, nutrition, and mental health with AI-driven recommendations",
                "A digital maintenance tracker that helps military units monitor equipment health, schedule repairs, and ensure mission readiness"
            ],
            REVIEW: ["Edit Details", "Create Project"]
        };

        const chips = CHATBOT_STEPS[stepToUse as keyof typeof CHATBOT_STEPS] || [];

        // Don't show chips during processing, when completed, or when project creation is in progress
        if (chips.length === 0 || isProcessing || (chatEngine.isComplete() && stepToUse !== "REVIEW")) return null;

        // Get user-friendly label for the step
        const getStepLabel = (step: string) => {
            const stepLabels = {
                TYPE: "Example project types to try",
                NAME: "Example project names to try",
                FILES: "File upload options",
                FILE_ANALYSIS: "File analysis options",
                USER: "Example personas to try",
                PROBLEMS: "Example pain points to try",
                SOLUTION: "Example solutions to try",
                REVIEW: "Review options"
            };
            return stepLabels[step as keyof typeof stepLabels] || `Examples for ${step}`;
        };

        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
                {/* User-friendly step label */}
                <Box sx={{ fontSize: "0.75rem", color: "text.secondary", opacity: 0.8, fontWeight: 500 }}>{getStepLabel(stepToUse)}</Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, maxHeight: 200, overflow: "auto" }}>
                    {chips.map((chipText, index) => (
                        <Chip
                            key={index}
                            label={chipText}
                            clickable
                            variant="outlined"
                            onClick={() => {
                                // Special handling for REVIEW step chips
                                if (stepToUse === "REVIEW") {
                                    if (chipText === "Edit Details") {
                                        handleRestart();
                                        return;
                                    } else if (chipText === "Create Project") {
                                        handleConfirmProject();
                                        return;
                                    }
                                }

                                // Regular chip processing for other steps
                                // Auto-advance: select chip and move to next step automatically
                                const response = chatEngine.selectChip(chipText);

                                // Add user message
                                const userMessage: ChatMessage = {
                                    id: Date.now().toString(),
                                    role: "user",
                                    content: chipText,
                                    timestamp: new Date()
                                };

                                // Add assistant response with NEW step (after processing)
                                const assistantMessage: ChatMessage = {
                                    id: (Date.now() + 1).toString(),
                                    role: "assistant",
                                    content: response,
                                    timestamp: new Date(),
                                    stepId: chatEngine.getCurrentStep() // This is the NEW step after processing
                                };

                                setMessages(prev => [...prev, userMessage, assistantMessage]);

                                // Update UI state based on current step
                                updateUIStateForCurrentStep();
                            }}
                            sx={{
                                fontSize: "0.875rem",
                                fontWeight: 500,
                                transition: "all 0.2s ease",
                                borderColor: "primary.main",
                                color: "primary.main",
                                "&:hover": {
                                    transform: "translateY(-1px)",
                                    boxShadow: 1,
                                    backgroundColor: "primary.50",
                                    borderColor: "primary.dark"
                                }
                            }}
                        />
                    ))}
                </Box>
            </Box>
        );
    };

    // Update UI state based on current conversation step
    const updateUIStateForCurrentStep = () => {
        const currentStep = chatEngine.getCurrentStep();

        if (currentStep === "FILES") {
            setShowFileUpload(true);
            setShowProjectPreview(false);
            setShowProjectSummary(false);
        } else if (currentStep === "REVIEW") {
            setShowProjectPreview(true);
            setShowFileUpload(false);
            setShowProjectSummary(false);
        } else if (currentStep === "COMPLETE" || chatEngine.isComplete()) {
            setShowProjectSummary(true);
            setShowProjectPreview(false);
            setShowFileUpload(false);
        } else {
            setShowProjectPreview(false);
            setShowFileUpload(false);
            setShowProjectSummary(false);
        }
    };

    // Render interactive elements based on current step
    const renderInteractiveElements = () => {
        // Always show chips for current step if available
        return renderCurrentStepChips();
    };

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {/* Header - MUI AppBar with Design Guidelines */}
            <AppBar
                position="static"
                elevation={0}
                sx={{
                    bgcolor: "primary.main",
                    borderRadius: 0,
                    // Sharp, unrounded corners at the bottom
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0
                }}
            >
                <Toolbar
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        minHeight: { xs: 56, sm: 64 }, // MUI standard AppBar heights
                        px: 3
                    }}
                >
                    {/* Left side - White Intelligenic Icon and Title/Subtitle */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
                        {/* White Intelligenic Icon */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                "& svg": {
                                    color: "#ffffff",
                                    filter: "brightness(0) invert(1)" // Ensures icon is white
                                }
                            }}
                        >
                            <Component250418IntIcon />
                        </Box>

                        {/* Title and Subtitle on separate lines */}
                        <Box>
                            <Typography
                                variant="h6"
                                component="div"
                                sx={{
                                    fontWeight: 600,
                                    color: "#ffffff",
                                    lineHeight: 1.2,
                                    fontSize: "1.125rem" // 18px for proper hierarchy
                                }}
                            >
                                Intelligenic Guidance Product Setup
                            </Typography>
                            <Typography
                                variant="body2"
                                component="div"
                                sx={{
                                    color: "rgba(255, 255, 255, 0.8)",
                                    fontSize: "0.875rem", // 14px for subtitle
                                    lineHeight: 1.2,
                                    mt: 0.25 // Small spacing between title and subtitle
                                }}
                            >
                                Define your project details and get started with Feature Studio.
                            </Typography>
                        </Box>
                    </Box>

                    {/* Right side - Close and Restart Conversation actions */}
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <IconButton
                            onClick={handleRestart}
                            size="medium"
                            sx={{
                                color: "#ffffff",
                                "&:hover": {
                                    backgroundColor: "rgba(255, 255, 255, 0.1)"
                                }
                            }}
                            title="Restart Conversation"
                        >
                            <RefreshIcon />
                        </IconButton>
                        {onClose && (
                            <IconButton
                                onClick={onClose}
                                size="medium"
                                sx={{
                                    color: "#ffffff",
                                    "&:hover": {
                                        backgroundColor: "rgba(255, 255, 255, 0.1)"
                                    }
                                }}
                                title="Close"
                            >
                                <CloseIcon />
                            </IconButton>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>
            THIS IS IT
        </Box>
    );
};

export default ProjectSetupChatbot;
